import { formatLocalDateKey } from "../../utils/dateRangeUtils"

/** Helpers for message-volume calendar heatmaps (no mock dashboard data). */

function toISODate(date) {
  return formatLocalDateKey(date)
}

const seedFrom = (input) =>
  Array.from(input).reduce((sum, char) => sum + char.charCodeAt(0), 0)

const seeded = (seed) => {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

const range = (seed, min, max) => min + seeded(seed) * (max - min)

export function getMessagesByCalendarDayFromSeries(combinedSeries, rangeStart, rangeEnd) {
  if (!Array.isArray(combinedSeries) || rangeStart == null || rangeEnd == null) {
    return { dates: [], values: [] }
  }
  const start = toISODate(rangeStart)
  const end = toISODate(rangeEnd)
  if (!start || !end) return { dates: [], values: [] }
  const rows = combinedSeries.filter((entry) => entry.date >= start && entry.date <= end)
  return {
    dates: rows.map((r) => r.date),
    values: rows.map((r) => r.messages),
  }
}

function distributeTotalAcrossHours(total, hourCount, seedBase) {
  if (hourCount <= 0) return []
  if (total <= 0) return Array(hourCount).fill(0)
  const weights = []
  for (let i = 0; i < hourCount; i += 1) {
    weights.push(range(seedBase + i * 17, 0.02, 1))
  }
  const sumW = weights.reduce((s, w) => s + w, 0)
  const floats = weights.map((w) => (total * w) / sumW)
  const ints = floats.map((x) => Math.floor(x))
  let rem = total - ints.reduce((s, x) => s + x, 0)
  let j = 0
  while (rem > 0) {
    ints[j % hourCount] += 1
    rem -= 1
    j += 1
  }
  return ints
}

const HOURS_PER_DAY = 24

function hourLabel12(h) {
  const x = h % 12
  const suffix = h < 12 ? "AM" : "PM"
  const num = x === 0 ? 12 : x
  return `${num}${suffix}`
}

const HOUR_LABELS_24 = Array.from({ length: HOURS_PER_DAY }, (_, h) => hourLabel12(h))

function trimMatrixToActiveHourRows(matrix, hourLabels) {
  if (!matrix.length || !hourLabels.length) return { matrix: [], hours: [] }
  let first = 0
  let last = matrix.length - 1
  while (first <= last && matrix[first].every((v) => Number(v) === 0)) first += 1
  while (last >= first && matrix[last].every((v) => Number(v) === 0)) last -= 1
  if (first > last) {
    return { matrix, hours: hourLabels }
  }
  return {
    matrix: matrix.slice(first, last + 1),
    hours: hourLabels.slice(first, last + 1),
  }
}

export function buildDailyCalendarHourlyMatrix(isoDates, dailyTotals) {
  const n = Math.min(isoDates.length, dailyTotals.length)
  const matrix = Array.from({ length: HOURS_PER_DAY }, () => Array(n).fill(0))
  for (let d = 0; d < n; d += 1) {
    const dayTotal = Math.max(0, Math.round(Number(dailyTotals[d]) || 0))
    const seed = seedFrom(`daily-hourly-${isoDates[d]}`)
    const parts = distributeTotalAcrossHours(dayTotal, HOURS_PER_DAY, seed)
    for (let h = 0; h < HOURS_PER_DAY; h += 1) {
      matrix[h][d] = parts[h]
    }
  }
  return trimMatrixToActiveHourRows(matrix, HOUR_LABELS_24)
}

export function buildMonthlyCalendarHourlyMatrix(isoDates, dailyTotals) {
  const byMonth = new Map()
  const n = Math.min(isoDates?.length ?? 0, dailyTotals?.length ?? 0)
  for (let d = 0; d < n; d += 1) {
    const iso = isoDates[d]
    if (!iso || typeof iso !== "string") continue
    const ym = iso.slice(0, 7)
    if (!/^\d{4}-\d{2}$/.test(ym)) continue
    const add = Math.max(0, Math.round(Number(dailyTotals[d]) || 0))
    byMonth.set(ym, (byMonth.get(ym) || 0) + add)
  }
  const monthKeys = [...byMonth.keys()].sort()
  if (monthKeys.length === 0) {
    return { matrix: [], hours: [], dates: [] }
  }
  const matrix = Array.from({ length: HOURS_PER_DAY }, () => Array(monthKeys.length).fill(0))
  for (let mi = 0; mi < monthKeys.length; mi += 1) {
    const total = byMonth.get(monthKeys[mi]) || 0
    const seed = seedFrom(`monthly-msg-hourly-${monthKeys[mi]}`)
    const parts = distributeTotalAcrossHours(total, HOURS_PER_DAY, seed)
    for (let h = 0; h < HOURS_PER_DAY; h += 1) {
      matrix[h][mi] = parts[h]
    }
  }
  const trimmed = trimMatrixToActiveHourRows(matrix, HOUR_LABELS_24)
  return { ...trimmed, dates: monthKeys }
}
